import React, { useState } from 'react';
import { Modal, Form, Spinner, Alert } from 'react-bootstrap';
import CompactUpgradePrompt from '../../../Components/CompactUpgradePrompt';
import '../agent-training.css';
import './AddWebsiteModal.css';
import { CancelButton, PrimaryButton } from '../../../Components/Buttons';
import { FiInfo } from 'react-icons/fi';
import { useLanguage } from '../../../Context/LanguageContext';

const AddWebsiteModal = ({
  show,
  onHide,
  workspaceId,
  apiUrl,
  onPollState,
  crawledUrlCount,
  crawlUrlLimit
}) => {
  const { t } = useLanguage();
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [includeEnabled, setIncludeEnabled] = useState(false);
  const [excludeEnabled, setExcludeEnabled] = useState(false);
  const [includePatterns, setIncludePatterns] = useState('');
  const [excludePatterns, setExcludePatterns] = useState('');

  // Check if URL limit is reached
  const isUrlLimitReached = crawlUrlLimit && crawledUrlCount >= crawlUrlLimit;

  const handleClose = () => {
    if (!loading) {
      setWebsite('');
      setError(null);
      setIncludeEnabled(false);
      setExcludeEnabled(false);
      setIncludePatterns('');
      setExcludePatterns('');
      onHide();
    }
  };

  const handleAddWebsite = async () => {
    try {
      setLoading(true);
      setError(null);
      const brandvoxToken = localStorage.getItem('brandvox_token');

      // Parse patterns from text areas (one pattern per line, filter empty lines)
      const parsedIncludePatterns = includeEnabled && includePatterns.trim()
        ? includePatterns.split('\n').map(p => p.trim()).filter(p => p)
        : null;
      const parsedExcludePatterns = excludeEnabled && excludePatterns.trim()
        ? excludePatterns.split('\n').map(p => p.trim()).filter(p => p)
        : null;

      const body = JSON.stringify({
        workspace_id: workspaceId,
        website: website,
        include_patterns: parsedIncludePatterns,
        exclude_patterns: parsedExcludePatterns,
      });

      const response = await fetch(`${apiUrl}/api/v1/train/add-website`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body,
      });

      if (!response.ok) {
        let errorMessage = t('dashboard.training.modals.addwebsitemodal.errorFailed');
        try {
          const errorData = await response.json();
          // Check for a specific error detail indicating the website is already scanned
          // Adjust the status code (e.g., 400 or 409) and detail message as needed
          if (response.status === 400 && errorData.message === 'Website already scanned') { // Example check
            errorMessage = t('dashboard.training.modals.addwebsitemodal.errorAlreadyScanned');
          } else if (errorData.detail) {
            // Use detail from API if available and not the specific 'already scanned' error
            errorMessage = errorData.detail;
          }
        } catch (jsonError) {
          // If parsing JSON fails, stick to the default error
        }
        throw new Error(errorMessage);
      }

      handleClose();
      await onPollState();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced URL validation
  const isValidUrl = (url) => {
    try {
      const trimmedUrl = url.trim();
      if (!trimmedUrl) return false;

      // Add https:// protocol if not provided
      const urlToCheck = /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
      const parsedUrl = new URL(urlToCheck);

      // Validate hostname has proper domain structure
      const hostname = parsedUrl.hostname;

      // Domain must contain at least one dot and have a valid TLD
      if (!hostname.includes('.')) return false;

      // Validate domain format: no invalid characters, proper structure
      const domainPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?)*\.[a-zA-Z]{2,}$/;
      if (!domainPattern.test(hostname)) return false;

      return true;
    } catch (e) {
      return false;
    }
  };

  // Extract domain from URL
  const extractDomain = (url) => {
    try {
      const trimmedUrl = url.trim();
      const urlToCheck = /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
      const parsedUrl = new URL(urlToCheck);
      return parsedUrl.hostname.replace(/^www\./, '');
    } catch (e) {
      return '';
    }
  };

  // Check if include patterns contain the website domain
  const validateIncludePatterns = () => {
    if (!includeEnabled || !includePatterns.trim() || !website.trim()) {
      return true;
    }

    const websiteDomain = extractDomain(website);
    if (!websiteDomain) return true;

    const patterns = includePatterns.split('\n').map(p => p.trim()).filter(p => p);
    const hasMatchingDomain = patterns.some(pattern => {
      const patternDomain = extractDomain(pattern);
      return patternDomain === websiteDomain;
    });

    return hasMatchingDomain;
  };

  const isWebsiteValid = website.trim() !== '' && isValidUrl(website);
  const isPatternsValid = validateIncludePatterns();

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={loading ? 'static' : true}
      keyboard={!loading}
      centered
      size="lg"
      className="add-website-modal"
    >
      <Modal.Header closeButton={!loading}>
        <Modal.Title>{t('dashboard.training.modals.addwebsitemodal.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant='danger' onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {isUrlLimitReached && (
          <CompactUpgradePrompt
            message={t('dashboard.training.modals.addwebsitemodal.upgradeMessage')}
            buttonText={t('dashboard.training.modals.addwebsitemodal.upgradeButton')}
            type="quota"
            percentage={100}
          />
        )}

        <Form>
          <Form.Group className='mb-3'>
            <Form.Label>
              {t('dashboard.training.modals.addwebsitemodal.urlLabel')}
            </Form.Label>
            <Form.Control
              type='text'
              value={website}
              placeholder={t('dashboard.training.modals.addwebsitemodal.urlPlaceholder')}
              onChange={(e) => setWebsite(e.target.value)}
              disabled={loading || isUrlLimitReached}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (isWebsiteValid && isPatternsValid && !loading && !isUrlLimitReached) {
                    handleAddWebsite();
                  }
                }
              }}
            />
            <Form.Text>
              {t('dashboard.training.modals.addwebsitemodal.urlHelpText')}
            </Form.Text>
          </Form.Group>

          {website && !isWebsiteValid && !isUrlLimitReached && (
            <Alert variant="warning" className="compact-alert">
              <FiInfo size={14} className="me-2" />
              {t('dashboard.training.modals.addwebsitemodal.urlInvalidWarning')}
            </Alert>
          )}

          {includeEnabled && includePatterns.trim() && !isPatternsValid && (
            <Alert variant="warning" className="compact-alert">
              <FiInfo size={14} className="me-2" />
              {t('dashboard.training.modals.addwebsitemodal.includeDomainWarning')}
            </Alert>
          )}

          {/* Include Patterns Section */}
          <div className="pattern-section">
            <Form.Check
              type="switch"
              id="include-patterns-switch"
              label={t('dashboard.training.modals.addwebsitemodal.includeToggle')}
              checked={includeEnabled}
              onChange={(e) => setIncludeEnabled(e.target.checked)}
              disabled={loading || isUrlLimitReached}
              className="pattern-toggle"
            />
            {includeEnabled && (
              <div className="pattern-input-container">
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={includePatterns}
                  placeholder={t('dashboard.training.modals.addwebsitemodal.includePlaceholder')}
                  onChange={(e) => setIncludePatterns(e.target.value)}
                  disabled={loading || isUrlLimitReached}
                  className="pattern-textarea"
                />
                <div className="pattern-hint">
                  <FiInfo size={14} />
                  <span>
                    {t('dashboard.training.modals.addwebsitemodal.includeHint', {
                      code: t('dashboard.training.modals.addwebsitemodal.includeHintCode')
                    })}
                  </span>
                </div>
                <div className="pattern-example">
                  <div className="pattern-example-title">{t('dashboard.training.modals.addwebsitemodal.includeExample')}</div>
                  <div className="pattern-example-text">
                    {t('dashboard.training.modals.addwebsitemodal.includeExampleText').split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i === 0 && <br />}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Exclude Patterns Section */}
          <div className="pattern-section">
            <Form.Check
              type="switch"
              id="exclude-patterns-switch"
              label={t('dashboard.training.modals.addwebsitemodal.excludeToggle')}
              checked={excludeEnabled}
              onChange={(e) => setExcludeEnabled(e.target.checked)}
              disabled={loading || isUrlLimitReached}
              className="pattern-toggle"
            />
            {excludeEnabled && (
              <div className="pattern-input-container">
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={excludePatterns}
                  placeholder={t('dashboard.training.modals.addwebsitemodal.excludePlaceholder')}
                  onChange={(e) => setExcludePatterns(e.target.value)}
                  disabled={loading || isUrlLimitReached}
                  className="pattern-textarea"
                />
                <div className="pattern-hint">
                  <FiInfo size={14} />
                  <span>
                    {t('dashboard.training.modals.addwebsitemodal.excludeHint', {
                      code: t('dashboard.training.modals.addwebsitemodal.excludeHintCode')
                    })}
                  </span>
                </div>
                <div className="pattern-example">
                  <div className="pattern-example-title">{t('dashboard.training.modals.addwebsitemodal.excludeExample')}</div>
                  <div className="pattern-example-text">
                    {t('dashboard.training.modals.addwebsitemodal.excludeExampleText').split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i === 0 && <br />}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <CancelButton className='cancel-btn' onClick={handleClose} disabled={loading}>
          {t('dashboard.training.modals.addwebsitemodal.cancelButton')}
        </CancelButton>
        <PrimaryButton
          className='primary-btn'
          onClick={handleAddWebsite}
          disabled={loading || !isWebsiteValid || !isPatternsValid || isUrlLimitReached}
        >
          {loading ? (
            <>
              <Spinner
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
                className='me-2'
              />
              {t('dashboard.training.modals.addwebsitemodal.addingButton')}
            </>
          ) : (
            t('dashboard.training.modals.addwebsitemodal.addButton')
          )}
        </PrimaryButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AddWebsiteModal;
